import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ArrowLeft, Users, Download, Eye } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => Math.round(parseFloat(n) || 0).toLocaleString('fr-FR');

const MONTH_LABELS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

export default function TableauInvestisseurs({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [financialData, setFinancialData] = useState(null);
  const [investorsList, setInvestorsList] = useState([]);
  const now = new Date();
  const [releveMonth, setReleveMonth] = useState(now.getMonth() + 1);
  const [releveYear, setReleveYear] = useState(now.getFullYear());
  const [downloading, setDownloading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/financial/dashboard`).catch(() => ({ data: {} })),
      axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/investisseurs`).catch(() => ({ data: { items: [] } })),
    ])
      .then(([fin, inv]) => {
        setFinancialData(fin.data || {});
        setInvestorsList(inv.data?.items || []);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  // Normalisation (supprime accents, trim, lowercase) pour matching robuste
  const normalize = (s) =>
    (s || '')
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();

  const userNameNorm = normalize(user?.name);
  const userEmailNorm = normalize(user?.email);

  const matchedInvestor = investorsList.find((inv) => {
    const fullName = normalize(`${inv.prenom || ''} ${inv.nom || ''}`);
    const fullNameReversed = normalize(`${inv.nom || ''} ${inv.prenom || ''}`);
    const invEmail = normalize(inv.email);
    const invNom = normalize(inv.nom);
    const invPrenom = normalize(inv.prenom);
    return (
      (userEmailNorm && invEmail === userEmailNorm) ||
      (userNameNorm && (fullName === userNameNorm || fullNameReversed === userNameNorm)) ||
      (userNameNorm && (invPrenom === userNameNorm || invNom === userNameNorm))
    );
  });

  const investorMise = matchedInvestor ? parseFloat(matchedInvestor.mise) || 0 : 0;
  const totalMises = investorsList.reduce((s, i) => s + (parseFloat(i.mise) || 0), 0);

  // Part de l'investisseur dans le pool 20% (Option A : prorata sur la mise)
  // shareInPool = mise_investisseur / total_des_mises
  const shareInPool = totalMises > 0 ? investorMise / totalMises : 0;

  const investorDisplayName = matchedInvestor
    ? `${matchedInvestor.prenom || ''} ${matchedInvestor.nom || ''}`.trim() || user?.name || 'Investisseur'
    : user?.name || 'Investisseur';

  // Données mensuelles RÉELLES depuis l'API (pas de mock)
  const monthlyTrend = financialData?.monthlyTrend || [];

  const downloadReleve = async () => {
    if (!matchedInvestor) {
      toast.error("Votre compte n'est pas lié à un investisseur");
      return;
    }
    setDownloading(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/investisseurs/releve?month=${releveMonth}&year=${releveYear}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Releve_${matchedInvestor.prenom || ''}_${matchedInvestor.nom || ''}_${releveYear}_${String(releveMonth).padStart(2, '0')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Relevé téléchargé');
    } catch (e) {
      console.error(e);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloading(false);
    }
  };

  const previewReleve = async () => {
    if (!matchedInvestor) {
      toast.error("Votre compte n'est pas lié à un investisseur");
      return;
    }
    setPreviewing(true);
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/investisseurs/releve?month=${releveMonth}&year=${releveYear}`,
        { responseType: 'blob' }
      );
      const blob = new Blob([res.data], { type: 'application/pdf' });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
    } catch (e) {
      console.error(e);
      toast.error("Erreur lors de l'aperçu");
    } finally {
      setPreviewing(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4 flex-wrap">
          <Button
            onClick={() => {
              const isInvestor = user?.role === 'investisseur' || user?.role === 'associe';
              navigate(-1);
            }}
            variant="outline"
            className="gap-2"
            data-testid="ti-back-btn"
          >
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Tableau Investissement</h1>
            <p className="text-gray-600 mt-1 text-sm">
              Suivi de votre investissement et de votre part au prorata (20% du bénéfice net)
            </p>
          </div>
        </div>

        {/* Avertissement si user non lié à un investisseur */}
        {!matchedInvestor && investorsList.length > 0 && (
          <Card className="border-2 border-amber-300 bg-amber-50">
            <CardContent className="p-6">
              <p className="font-bold text-amber-900 mb-2">⚠️ Votre compte n'est pas encore lié à un investisseur</p>
              <p className="text-sm text-amber-800 mb-2">
                Vous êtes connecté(e) avec <strong>{user?.name || '-'}</strong> ({user?.email || '-'}).<br />
                Aucun investisseur enregistré ne correspond à ces informations.
              </p>
              <p className="text-sm text-amber-800">
                Demandez à l'administrateur d'ajouter votre email (<strong>{user?.email}</strong>) au champ "Email" de votre fiche dans <strong>Entreprise → Investisseurs</strong>. Puis rafraîchissez cette page.
              </p>
              <div className="mt-3">
                <Button onClick={() => window.location.reload()} variant="outline" className="gap-2 border-amber-600 text-amber-700 hover:bg-amber-100">
                  Rafraîchir
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Carte centrale Total Investi */}
        <div className="flex justify-center">
          <Card
            className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-purple-600 text-white w-full md:w-1/3"
            data-testid="investor-summary-card"
          >
            <CardContent className="p-8">
              <div className="text-center">
                <p className="text-xl font-semibold opacity-95">{investorDisplayName}</p>
                <p className="text-sm opacity-75 mt-2">Total Investi</p>
                <p className="text-5xl font-bold mt-4" data-testid="investor-mise-amount">
                  {fmt(investorMise)} €
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Téléchargement du relevé PDF mensuel */}
        {matchedInvestor && (
          <Card className="bg-white shadow-sm border border-purple-100" data-testid="releve-pdf-card">
            <CardContent className="p-5">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="font-semibold text-gray-900">Relevé mensuel PDF</p>
                  <p className="text-xs text-gray-500 mt-1">
                    Téléchargez le détail de votre mise, part au prorata et dividende du mois.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    className="border rounded px-2 py-2 text-sm bg-white"
                    value={releveMonth}
                    onChange={(e) => setReleveMonth(parseInt(e.target.value, 10))}
                    data-testid="releve-month-select"
                  >
                    {MONTH_LABELS_FR.map((lbl, idx) => (
                      <option key={idx} value={idx + 1}>{lbl}</option>
                    ))}
                  </select>
                  <select
                    className="border rounded px-2 py-2 text-sm bg-white"
                    value={releveYear}
                    onChange={(e) => setReleveYear(parseInt(e.target.value, 10))}
                    data-testid="releve-year-select"
                  >
                    {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => (
                      <option key={y} value={y}>{y}</option>
                    ))}
                  </select>
                  <Button
                    onClick={previewReleve}
                    disabled={previewing}
                    variant="outline"
                    className="gap-2 border-purple-300 text-purple-700 hover:bg-purple-50"
                    data-testid="preview-releve-btn"
                  >
                    <Eye className="h-4 w-4" />
                    {previewing ? '...' : 'Aperçu'}
                  </Button>
                  <Button
                    onClick={downloadReleve}
                    disabled={downloading}
                    className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
                    data-testid="download-releve-btn"
                  >
                    <Download className="h-4 w-4" />
                    {downloading ? 'Génération...' : 'Télécharger PDF'}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tableau Investissement avec colonne prorata */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gradient-to-r from-purple-600 to-purple-700 text-white">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-6 w-6" /> Mon Investissement
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-purple-200 bg-purple-50">
                    <th className="px-4 py-3 text-left font-semibold text-purple-900 text-sm">Mois</th>
                    <th className="px-4 py-3 text-center font-semibold text-purple-900 text-sm">Bénéfice Net</th>
                    <th className="px-4 py-3 text-center font-semibold text-purple-900 text-sm">
                      Part Investisseurs 20%
                    </th>
                    <th className="px-4 py-3 text-center font-semibold text-purple-900 text-sm">
                      Ma part au prorata
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyTrend.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-12 text-center text-gray-400 italic">
                        Aucune donnée mensuelle disponible
                      </td>
                    </tr>
                  ) : (
                    monthlyTrend.map((m, index) => {
                      const benef = parseFloat(m.profit) || 0;
                      const pool20 = benef * 0.2;
                      const myShare = pool20 * shareInPool;
                      return (
                        <tr
                          key={index}
                          className={`border-b ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-purple-50`}
                        >
                          <td className="px-4 py-3 font-medium text-sm">{m.month}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-block bg-green-100 text-green-700 px-2 py-1 rounded-full font-semibold text-sm">
                              {fmt(benef)} €
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-block bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-semibold text-sm">
                              {fmt(pool20)} €
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-block bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-bold text-sm">
                              {fmt(myShare)} €
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}

                  {monthlyTrend.length > 0 && (() => {
                    const totalBenef = monthlyTrend.reduce((s, m) => s + (parseFloat(m.profit) || 0), 0);
                    const totalPool = totalBenef * 0.2;
                    const totalMyShare = totalPool * shareInPool;
                    return (
                      <tr className="bg-purple-100 border-t-2 border-purple-300">
                        <td className="px-4 py-4 font-bold text-gray-800">TOTAL</td>
                        <td className="px-4 py-4 text-center font-bold text-green-700 text-base">
                          {fmt(totalBenef)} €
                        </td>
                        <td className="px-4 py-4 text-center font-bold text-blue-700 text-base">
                          {fmt(totalPool)} €
                        </td>
                        <td className="px-4 py-4 text-center font-bold text-purple-700 text-base">
                          {fmt(totalMyShare)} €
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Explication du calcul */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-6">
            <h3 className="font-bold text-blue-900 mb-3">Calcul de la part au prorata (Option A)</h3>
            <div className="space-y-2 text-sm text-blue-800">
              <p>
                • <strong>20% du bénéfice net</strong> est mis dans un pool destiné à tous les investisseurs.
              </p>
              <p>
                • Votre part = (votre mise ÷ total des mises) × 20% × bénéfice mensuel
              </p>
              {totalMises > 0 && (
                <p className="font-medium">
                  • Calcul pour vous : {fmt(investorMise)} ÷ {fmt(totalMises)} = {(shareInPool * 100).toFixed(2)}% du pool
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Modal Aperçu PDF */}
        <Dialog open={!!previewUrl} onOpenChange={(o) => !o && closePreview()}>
          <DialogContent className="max-w-5xl w-full h-[90vh] flex flex-col p-0" data-testid="releve-preview-modal">
            <DialogHeader className="px-6 py-3 border-b">
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-purple-600" />
                Aperçu — {MONTH_LABELS_FR[releveMonth - 1]} {releveYear}
              </DialogTitle>
            </DialogHeader>
            <div className="flex-1 bg-gray-100 overflow-hidden">
              {previewUrl && (
                <iframe
                  src={previewUrl}
                  title="Aperçu du relevé PDF"
                  className="w-full h-full border-0"
                  data-testid="releve-preview-iframe"
                />
              )}
            </div>
            <div className="flex justify-end gap-2 px-6 py-3 border-t bg-white">
              <Button variant="outline" onClick={closePreview} data-testid="close-preview-btn">
                Fermer
              </Button>
              <Button
                onClick={downloadReleve}
                disabled={downloading}
                className="gap-2 bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Download className="h-4 w-4" />
                {downloading ? 'Génération...' : 'Télécharger'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
